import {
  Close,
  CloseFullscreen,
  Code,
  ContentCopy,
  Delete,
  Download,
  DragHandleOutlined,
  Info,
  OpenInFull,
  Refresh,
  Report,
  Send,
  Stop,
  ThumbDown,
  ThumbDownOutlined,
  ThumbUp,
  ThumbUpOutlined,
  Warning
} from '@mui/icons-material'
import {
  Box,
  IconButton,
  Tooltip,
  styled,
  tooltipClasses,
  iconButtonClasses,
  OutlinedInput,
  FormControl,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  ToggleButton,
  Typography,
  keyframes,
  List,
  Divider,
  FormControlLabel,
  Button,
  buttonClasses,
  outlinedInputClasses,
  formControlLabelClasses,
  ListItem,
  ListItemText,
  FormHelperText,
  Checkbox,
  Backdrop,
  Chip,
  MenuItem,
  Menu
} from '@mui/material'

export const StyledBox = styled(Box)(({theme}) => ({
  position: 'fixed',
  right: '20px',
  bottom: '20px',
  zIndex: theme.zIndex.fab
}))

export const StyledToolTip = styled(({className, ...props}) => <Tooltip {...props} classes={{popper: className}} />)(
  ({theme}) => ({
    [`&.${tooltipClasses.popper}`]: {
      zIndex: theme.zIndex.tooltip
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.hpBaseDarker
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.hpBaseDarker
    }
  })
)

export const StyledChatIconToolTip = styled(({className, ...props}) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    width: '190px',
    border: `1px solid ${theme.palette.hpAccentPurple}`,
    backgroundColor: theme.palette.hpPurpleLightest,
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.secondary.dark,
    lineHeight: theme.typography.h6.lineHeight,
    borderRadius: '10px',
    position: 'relative',
    right: '20px'
  },
  [`& .${tooltipClasses.arrow}`]: {
    fontSize: theme.typography.kpiMedium.fontSize,
    left: '20px !important',
    color: theme.palette.hpPurpleLightest,
    '&:before': {
      backgroundColor: theme.palette.hpPurpleLightest,
      border: `1px solid ${theme.palette.hpAccentPurple}`
    }
  }
}))

export const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'customMargin' && prop !== 'sendButton'
})(({customMargin, sendButton}) => ({
  [`&.${iconButtonClasses.root}`]: {
    margin: customMargin || '0px',
    border: `none`,
    height: '30px',
    width: '30px',
    '&:disabled': {
      opacity: 0.4,
      pointerEvents: sendButton ? 'auto' : 'none'
    }
  }
}))

export const StyledChatIconButton = styled(IconButton)(({theme}) => ({
  [`&.${iconButtonClasses.root}`]: {
    backgroundColor: theme.palette.secondary.dark,
    width: '60px',
    height: '60px',
    '& svg': {
      fill: 'white'
    }
  }
}))

export const StyledChatDownloadIconButton = styled(IconButton)(() => ({
  [`&.${iconButtonClasses.root}`]: {
    border: `none`,
    height: '30px',
    width: '30px',
    '&:disabled': {
      opacity: 0.4
    }
  }
}))

export const StyledTitleContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `2px solid ${theme.palette.grey[600]}`,
  borderTop: `2px solid ${theme.palette.grey[600]}`,
  padding: '4px 6px'
}))

export const StyledTitle = styled(Box)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.hpAccentBlack,
  letterSpacing: '0px',
  margin: '4px 0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

export const StyledChatWindowParentContainer = styled(Box, {shouldForwardProp: (prop) => prop !== 'showChatWindow'})(
  ({showChatWindow}) => ({
    display: showChatWindow ? 'block' : 'none'
  })
)

export const StyledChatWindowContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  height: 'calc(100vh - 40px)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 2,
  marginTop: '40px',
  backgroundColor: theme.palette.hpWhite,
  right: 0,
  bottom: 0,
  top: 0,
  flexGrow: 1,
  flexShrink: 0
}))

export const StyledChatWindowSubContainer = styled(Box)(() => ({
  display: 'flex',
  flexGrow: 1
}))

export const StyledChatWindow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'minWidth' && prop !== 'maxWidth'
})(({width, minWidth, maxWidth}) => ({
  width,
  minWidth,
  maxWidth
}))

export const DragHandleBar = styled(Box)(({theme}) => ({
  flexShrink: 0,
  height: '100%',
  display: 'flex',
  position: 'relative',
  width: '10px',
  backgroundColor: theme.palette.hpBlack,
  cursor: 'col-resize'
}))

export const DragHandle = styled(Box)(({theme}) => ({
  height: '30px',
  width: '10px',
  left: '-90%',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  border: `none`,
  backgroundColor: theme.palette.hpBlack
}))

export const TopTriangle = styled(Box)(({theme}) => ({
  position: 'relative',
  top: '-30%',
  height: '0px',
  borderBottom: `10px solid ${theme.palette.hpBlack}`,
  borderLeft: '10px solid transparent'
}))

export const BottomTriangle = styled(Box)(({theme}) => ({
  position: 'relative',
  top: '66%',
  height: '0px',
  borderTop: `10px solid ${theme.palette.hpBlack}`,
  borderLeft: '10px solid transparent'
}))

export const VerticalLines = styled(DragHandleOutlined)(({theme}) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  left: '4px',
  border: `2px solid ${theme.palette.hpWhite}`,
  borderTop: 'none',
  borderBottom: 'none',
  width: '6px',
  height: '20px'
}))

export const StyledFormControl = styled(FormControl)(() => ({
  display: 'flex',
  flexDirection: 'row'
}))

export const StyledFeedbackInputFormControl = styled(FormControl)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingLeft: '30px'
}))

export const StyledFormHelperText = styled(FormHelperText, {shouldForwardProp: (prop) => prop !== 'error'})(
  ({error, theme}) => ({
    margin: '4px 0px',
    color: theme.palette.hpAccentRed,
    fontSize: error ? theme.typography.subtitle1.fontSize : theme.typography.subtitle2.fontSize,
    fontWeight: error ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular
  })
)

export const StyledCheckboxFormControl = styled(FormControl)(() => ({
  display: 'flex',
  flexDirection: 'row'
}))

export const StyledBodyContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100% - 148px)',
  scrollBehavior: 'smooth',
  overflowY: 'auto',
  paddingRight: '2px',
  margin: '0px 2px',
  position: 'relative'
}))

export const StyledWarningInfoContainer = styled(Box)(({theme}) => ({
  color: theme.palette.hpAccentBlack,
  backgroundColor: theme.palette.hpWhite,
  zIndex: theme.zIndex.drawer,
  position: 'sticky',
  top: '0px',
  padding: theme.spacing(0.75)
}))

export const StyledInfoBox = styled(Box, {shouldForwardProp: (prop) => prop !== 'type'})(({type, theme}) => ({
  padding: theme.spacing(0.75),
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: theme.palette.hpAccentBlack,
  position: 'relative',
  top: type === 'warning' ? '0px' : '-4px',
  border: type === 'warning' ? `1px solid ${theme.palette.hpAccentOrange}` : `1px solid ${theme.palette.hpPrimary}`,
  borderRadius: type === 'warning' ? '0px' : '8px',
  backgroundColor: type === 'warning' ? theme.palette.warning.light : theme.palette.hpPrimaryLightest,
  animation: type === 'warning' ? 'slideIn 0.5s ease' : 'arise 0.5s ease',
  '@keyframes slideIn': {
    '0%': {
      transform: 'translateY(-100%)'
    },
    '100%': {
      transform: 'translateY(0%)'
    }
  },
  '@keyframes arise': {
    '0%': {
      transform: 'translateX(100%)'
    },
    '100%': {
      transform: 'translateX(0%)'
    }
  }
}))

export const StyledInfo = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  color: theme.palette.hpAccentBlack,
  lineHeight: theme.typography.subtitle1.lineHeight,
  marginLeft: '6px'
}))

export const StyledBannerIconButton = styled(IconButton)(() => ({
  [`&.${iconButtonClasses.root}`]: {
    border: `none`,
    height: '30px',
    width: '30px',
    display: 'flex',
    alignSelf: 'flex-start'
  }
}))

export const StyledConfirmationWarningIcon = styled(Report)(({theme}) => ({
  color: theme.palette.hpAccentOrange,
  marginRight: '10px',
  marginTop: '2px',
  display: 'flex',
  alignSelf: 'flex-start'
}))

export const StyledWarningIcon = styled(Warning)(({theme}) => ({
  color: theme.palette.hpAccentOrange,
  display: 'flex',
  alignSelf: 'flex-start'
}))

export const StyledBannerCloseIcon = styled(Close)(({theme}) => ({
  color: theme.palette.hpBase
}))

export const StyledMessageContainer = styled(Box, {shouldForwardProp: (prop) => prop !== 'disableSelection'})(
  ({disableSelection}) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: '3px',
    marginLeft: '5px',
    userSelect: disableSelection ? 'none' : 'auto'
  })
)

export const StyledUserMessageBox = styled(Box, {shouldForwardProp: (prop) => prop !== 'isPrompt'})(
  ({isPrompt, theme}) => ({
    backgroundColor: isPrompt ? 'transparent' : theme.palette.hpPrimaryLightest,
    border: isPrompt ? `1px solid ${theme.palette.hpPrimary}` : '',
    cursor: isPrompt ? 'pointer' : '',
    color: theme.palette.hpAccentBlack,
    borderRadius: '8px',
    margin: '8px 4px 4px 2px',
    padding: '8px 12px',
    display: 'flex',
    alignSelf: 'flex-end',
    maxWidth: '85%',
    fontSize: theme.typography.subtitle1.fontSize,
    lineHeight: theme.typography.subtitle1.lineHeight,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    '&:hover': {
      backgroundColor: theme.palette.hpPrimaryLightest
    }
  })
)

export const StyledName = styled(Typography, {shouldForwardProp: (prop) => prop !== 'messageType'})(
  ({messageType, theme}) => ({
    marginBottom: '-5px',
    paddingLeft: messageType === 'Bot' ? '12px' : '0px',
    paddingRight: messageType === 'User' ? '12px' : '0px',
    textAlign: messageType === 'Bot' ? 'left' : 'right',
    color: theme.palette.hpAccentBlack
  })
)

export const StyledBotMessageBox = styled(Box)(({theme}) => ({
  color: theme.palette.hpAccentBlack,
  fontSize: theme.typography.subtitle1.fontSize,
  lineHeight: theme.typography.subtitle1.lineHeight,
  border: `1px solid ${theme.palette.hpBase}`,
  borderRadius: '8px',
  margin: '5px 2px',
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  wordBreak: 'break-word',
  width: 'fit-content',
  maxWidth: '85%'
}))

export const StyledSourceCodeIconButton = styled(IconButton)(() => ({
  [`&.${iconButtonClasses.root}`]: {
    border: `none`,
    height: '30px',
    width: '30px',
    marginLeft: '-8px'
  }
}))

export const StyledSourceCodeBox = styled(Box)(({theme}) => ({
  borderRadius: '8px',
  padding: theme.spacing(1),
  marginTop: '8px',
  color: theme.palette.hpAccentGreenVivid,
  backgroundColor: theme.palette.hpBlack,
  fontSize: theme.typography.subtitle2.fontSize,
  wordBreak: 'break-word'
}))

export const StyledButton = styled(Button)(({theme}) => ({
  [`&.${buttonClasses.root}`]: {
    border: `1px solid ${theme.palette.hpPrimary}`,
    borderRadius: '4px',
    backgroundColor: theme.palette.hpBaseLightest,
    padding: '4px 8px'
  }
}))

export const StyledChartContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  overflowX: 'auto'
}))

export const StyledChartIconsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  marginTop: theme.spacing(0.5)
}))

export const StyledDownloadIconButton = styled(IconButton)(() => ({
  [`&.${iconButtonClasses.root}`]: {
    border: `none`,
    height: '30px',
    width: '30px',
    marginLeft: '10px'
  }
}))

export const StyledFeedbackIconsContainer = styled(Box, {shouldForwardProp: (prop) => prop !== 'showSourceCode'})(
  ({showSourceCode, theme}) => ({
    display: 'flex',
    justifyContent: showSourceCode ? 'space-between' : 'flex-end',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
    marginRight: '-8px'
  })
)

export const StyledFeedbackIconButton = styled(IconButton)(() => ({
  [`&.${iconButtonClasses.root}`]: {
    border: `none`,
    height: '30px',
    width: '30px',
    marginLeft: '4px',
    '&:disabled': {
      opacity: 0.8
    }
  }
}))

export const StyledExpandIcon = styled(OpenInFull)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledShrinkIcon = styled(CloseFullscreen)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledClearHistoryIcon = styled(Delete)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledDownloadIcon = styled(Download)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledCloseIcon = styled(Close)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledSourceCodeIcon = styled(Code)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledCopyIcon = styled(ContentCopy)(({theme}) => ({
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.h6.fontSize
}))

export const StyledSendIcon = styled(Send)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledThumbUpIconFilled = styled(ThumbUp)(({theme}) => ({
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.h6.fontSize
}))

export const StyledThumbDownIconFilled = styled(ThumbDown)(({theme}) => ({
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.h6.fontSize
}))

export const StyledThumbUpIcon = styled(ThumbUpOutlined)(({theme}) => ({
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.h6.fontSize
}))

export const StyledThumbDownIcon = styled(ThumbDownOutlined)(({theme}) => ({
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.h6.fontSize
}))

export const StyledFooterContainer = styled(Box, {shouldForwardProp: (prop) => prop !== 'showFullScreen'})(
  ({showFullScreen}) => ({
    position: 'absolute',
    bottom: '4px',
    right: '4x',
    left: '4px',
    marginLeft: showFullScreen ? '0px' : '10px',
    width: showFullScreen ? 'calc(100% - 8px)' : 'calc(100% - 20px)'
  })
)

export const StyledCategoryContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
  width: '100%',
  position: 'relative'
}))

export const StyledInfoTextBox = styled(Box)(({theme}) => ({
  paddingTop: theme.spacing(1)
}))
export const StyledInfoIconButton = styled(IconButton)(() => ({
  [`&.${iconButtonClasses.root}`]: {
    border: `none`,
    height: '26px',
    width: '26px',
    position: 'absolute',
    top: '0px',
    right: '12px'
  }
}))

export const StyledInfoIcon = styled(Info)(({theme}) => ({
  color: theme.palette.hpPrimary
}))

export const StyledToggleButtonGroupContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  width: 'fit-content'
}))

export const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
  [`& .${toggleButtonGroupClasses.root}`]: {
    display: 'flex',
    justifyContent: 'center'
  }
}))

export const StyledToggleButton = styled(ToggleButton)(({theme}) => ({
  color: theme.palette.grey[600],
  textTransform: 'none',
  fontSize: theme.typography.subtitle2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: theme.typography.subtitle2.lineHeight,
  height: '28px',
  minWidth: '115px',
  letterSpacing: '0.1px',
  display: 'flex',
  alignItems: 'center',
  '&.Mui-selected': {
    backgroundColor: theme.palette.hpPrimaryDark,
    color: theme.palette.hpWhite,
    '&:hover': {
      backgroundColor: theme.palette.hpPrimaryDark,
      color: theme.palette.hpWhite
    }
  }
}))

export const StyledOutlinedInput = styled(OutlinedInput)(({theme}) => ({
  [`&.${outlinedInputClasses.root}`]: {
    minWidth: '100%',
    minHeight: '48px',
    padding: '0 16px',
    letterSpacing: '0px',
    color: theme.palette.hpAccentBlack,
    fontSize: theme.typography.bodyMedium.fontSize
  },
  '&.Mui-focused > .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.hpPrimaryDark
  }
}))

export const StyledInputInfoBox = styled(Box, {shouldForwardProp: (prop) => prop !== 'error' && prop !== 'metric'})(
  ({metric, error}) => ({
    display: 'flex',
    justifyContent: metric === 'other' && error ? 'space-between' : 'flex-end'
  })
)

export const StyledMaxLengthBox = styled(Box)(({theme}) => ({
  fontSize: theme.typography.subtitle2.fontSize
}))

export const StyledFlexBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
}))

export const StyledBackdrop = styled(Backdrop)(({theme}) => ({
  zIndex: theme.zIndex.drawer + 1
}))

export const StyledFeedbackTitleContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '0px -12px'
}))

export const StyledDialogTitle = styled(Typography)(({theme}) => ({
  color: theme.palette.hpAccentBlack
}))

export const StyledFeedbackThumbDownIcon = styled(ThumbDownOutlined)(({theme}) => ({
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.h5.fontSize,
  marginRight: '6px',
  marginTop: '2px'
}))

export const StyledFeedbackContent = styled(Box)(({theme}) => ({
  margin: '15px 18px',
  fontSize: theme.typography.h6.fontSize
}))

export const StyledBoldText = styled(Box)(({theme}) => ({
  lineHeight: theme.typography.subtitle1.lineHeight,
  marginBottom: '15px',
  fontWeight: theme.typography.fontWeightSemibold,
  color: theme.palette.hpAccentBlack
}))

export const StyledFeedbackSuccessContent = styled(Box)(() => ({
  minHeight: '35vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column'
}))

export const StyledAIInfoBox = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.hpBaseLighter,
  fontSize: theme.typography.subtitle2.fontSize,
  fontStyle: 'italic',
  margin: '4px -12px -8px -12px',
  padding: '6px 10px',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  overflow: 'hidden',
  position: 'relative',
  letterSpacing: '0.3px'
}))

export const StyledSuccessMsg = styled(Typography)(({theme}) => ({
  color: theme.palette.hpAccentBlack,
  marginTop: '30px'
}))

export const StyledCheckbox = styled(Checkbox)(({theme}) => ({
  marginTop: '-6px',
  color: theme.palette.hpPrimaryDark,
  '&.Mui-checked': {
    color: theme.palette.hpPrimaryDark
  }
}))

export const StyledLabelBox = styled(Box)(({theme}) => ({
  color: theme.palette.grey[600]
}))

export const StyledFeedbackFooter = styled(Box, {shouldForwardProp: (prop) => prop !== 'error'})(({error}) => ({
  margin: '15px 34px',
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: error ? 'space-between' : 'flex-end'
}))

export const StyledConfirmationDialogButton = styled(Button, {shouldForwardProp: (prop) => prop !== 'buttonType'})(
  ({buttonType, theme}) => ({
    [`&.${buttonClasses.root}`]: {
      marginRight: buttonType === 'cancel' ? '10px' : '15px',
      border: buttonType === 'cancel' ? `1px solid ${theme.palette.hpPrimaryDark}` : 'none',
      color: buttonType === 'cancel' ? theme.palette.hpPrimaryDark : theme.palette.hpWhite,
      backgroundColor: buttonType === 'cancel' ? theme.palette.hpWhite : theme.palette.hpPrimary,
      fontSize: theme.typography.subtitle1.fontSize,
      textTransform: 'none',
      padding: '12px 20px',
      letterSpacing: '0.3px',
      '&:hover': {
        backgroundColor: buttonType === 'cancel' ? theme.palette.hpWhite : theme.palette.hpPrimary,
        color: buttonType === 'cancel' ? theme.palette.hpPrimaryDark : theme.palette.hpWhite
      }
    }
  })
)

export const StyledTextButton = styled(Button)(({theme}) => ({
  [`&.${buttonClasses.root}`]: {
    border: 'none',
    backgroundColor: theme.palette.hpWhite,
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.hpPrimaryDark,
    textTransform: 'none',
    marginRight: '6px',
    letterSpacing: '0.3px',
    '&:hover': {
      backgroundColor: theme.palette.hpWhite,
      color: theme.palette.hpPrimaryDark
    }
  }
}))

export const StyledFilledButton = styled(Button)(({theme}) => ({
  [`&.${buttonClasses.root}`]: {
    border: 'none',
    fontSize: theme.typography.subtitle1.fontSize,
    backgroundColor: theme.palette.hpPrimaryDark,
    textTransform: 'none',
    padding: '6px 20px',
    letterSpacing: '0.3px',
    '&:hover': {
      backgroundColor: theme.palette.hpPrimaryDark,
      color: theme.palette.hpWhite
    },
    '&:disabled': {
      color: theme.palette.hpWhite,
      opacity: 0.4
    }
  }
}))

export const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  [`&.${formControlLabelClasses.root}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '6px',
    paddingBottom: '6px'
  }
}))

export const StyledList = styled(List)(({theme}) => ({
  border: `1px solid ${theme.palette.hpBase}`,
  borderColor: 'divider',
  borderRadius: '5px',
  padding: 0
}))

export const StyledListItem = styled(ListItem)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  minHeight: '60px'
}))

export const StyledDivider = styled(Divider)(({theme}) => ({
  background: theme.palette.hpBase
}))

export const BouncingLoaderContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  margin: '15px 6px 6px 6px'
})

export const BouncingLoaderDiv = styled('div')(({theme}) => ({
  width: '10px',
  height: '10px',
  margin: theme.spacing(0.25),
  background: theme.palette.hpBaseDark,
  borderRadius: '50%',
  animation: `${bouncingLoader} 0.5s infinite alternate`,
  '&:nth-of-type(2)': {
    animationDelay: '0.15s'
  },
  '&:nth-of-type(3)': {
    animationDelay: '0.3s'
  }
}))

const bouncingLoader = keyframes`
  to {
    opacity: 0.4;
    transform: translate3d(0, -14px, 0);
  }
`
export const StyledChip = styled(Chip)(({theme}) => ({
  backgroundColor: theme.palette.hpAccentOrange,
  color: theme.palette.hpWhite,
  borderRadius: theme.spacing(2.5),
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.subtitle2.fontSize,
  padding: theme.spacing(0.5),
  height: theme.spacing(3.25),
  cursor: 'pointer'
}))

export const StyledBoxContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1)
}))

export const StyledActionButtonsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  width: '100%'
}))

export const StyledActionButtonsLeft = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}))

export const StyledActionButtonsRight = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center'
}))

export const StyledCopyActionButton = styled(IconButton)(({theme}) => ({
  [`&.${iconButtonClasses.root}`]: {
    minWidth: theme.spacing(3),
    width: theme.spacing(3),
    height: theme.spacing(3),
    padding: theme.spacing(0.5),
    '&:hover': {
      opacity: 1
    }
  }
}))

export const StyledRegenerateIcon = styled(Refresh)(({theme}) => ({
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.h6.fontSize
}))

export const StyledRegenerateActionButton = styled(IconButton)(({theme}) => ({
  [`&.${iconButtonClasses.root}`]: {
    minWidth: theme.spacing(3),
    width: theme.spacing(3),
    height: theme.spacing(3),
    padding: theme.spacing(0.5),
    '&:hover': {
      opacity: 1
    }
  }
}))

export const StyledDownloadActionButton = styled(IconButton)(({theme}) => ({
  [`&.${iconButtonClasses.root}`]: {
    minWidth: theme.spacing(3),
    width: theme.spacing(3),
    height: theme.spacing(3),
    padding: theme.spacing(0.5),
    '&:hover': {
      opacity: 1
    }
  }
}))

export const StyledStopActionButton = styled(IconButton)(({theme}) => ({
  [`&.${iconButtonClasses.root}`]: {
    color: theme.palette.hpPrimary,
    border: `1px solid ${theme.palette.hpPrimary}`,
    borderRadius: '50%',
    minWidth: theme.spacing(4),
    width: theme.spacing(4),
    height: theme.spacing(4),
    padding: theme.spacing(0.75),
    '&:hover': {
      borderColor: theme.palette.hpPrimaryDark,
      backgroundColor: theme.palette.hpPrimaryLightest
    }
  }
}))

export const StyledStopIcon = styled(Stop)(({theme}) => ({
  fontSize: theme.typography.kpiMedium.fontSize
}))

export const FlexCenterBox = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}))

export const SmallIcon = styled(Box)(({theme}) => ({
  fontSize: theme.typography.subtitle1.fontSize
}))

export const BoldNameText = styled(Typography)(({theme}) => ({
  fontWeight: theme.typography.fontWeightBold,
  textAlign: 'right',
  marginRight: '4px',
  paddingRight: '4px'
}))

export const BoldNameTextWithSpacing = styled(Typography)(({theme}) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginLeft: '4px',
  marginTop: '2px',
  marginBottom: '2px'
}))

export const CompactButton = styled(Box)(({theme}) => ({
  padding: '6px 12px',
  fontSize: theme.typography.subtitle1.fontSize
}))

export const PlotContainer = styled(Box)(() => ({
  width: '100%',
  height: '400px',
  minHeight: '400px',
  maxHeight: '400px',
  overflowX: 'auto',
  overflowY: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0
}))

export const getPlotlyTitleConfig = (wrappedTitle, theme) => ({
  text: wrappedTitle,
  x: 0.5,
  xref: 'paper',
  xanchor: 'center',
  y: 1.12,
  yref: 'paper',
  yanchor: 'bottom',
  showarrow: false,
  font: {
    size: 14,
    color: theme.palette.secondary.dark
  },
  bgcolor: 'transparent',
  bordercolor: 'transparent'
})

export const calculatePlotlyMargin = (titleLines, existingMargin = {}) => ({
  ...existingMargin,
  t: Math.max(70, 50 + titleLines * 20),
  b: 40,
  l: 50,
  r: 50
})

export const SmallDownloadIcon = styled(StyledDownloadIcon)(({theme}) => ({
  fontSize: theme.typography.subtitle1.fontSize
}))

export const SmallSourceCodeIcon = styled(StyledSourceCodeIcon)(({theme}) => ({
  fontSize: theme.typography.subtitle1.fontSize
}))

export const SmallCopyIcon = styled(StyledCopyIcon)(({theme}) => ({
  fontSize: theme.typography.subtitle1.fontSize
}))

export const CompactMenuItem = styled(MenuItem)(({theme}) => ({
  padding: '6px 12px',
  fontSize: theme.typography.body2.fontSize
}))

export const CompactMenuItemText = styled(ListItemText)(({theme}) => ({
  '& .MuiListItemText-primary': {
    fontSize: theme.typography.body2.fontSize
  }
}))

export const StyledDownloadMenu = styled(Menu)(({theme}) => ({
  zIndex: theme.zIndex.modal + 1,
  '& .MuiPaper-root': {
    minWidth: '140px',
    marginTop: '4px',
    boxShadow: `0 4px 12px ${theme.palette.grey[300]}`,
    zIndex: theme.zIndex.modal + 1
  }
}))

export const DownloadMenuHeader = styled(Box)(() => ({
  padding: '8px 12px 4px 12px',
  marginBottom: '4px'
}))

export const DownloadMenuTitle = styled(Box)(({theme}) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}))

export const downloadMenuListProps = {
  dense: true,
  sx: {
    padding: '0'
  }
}

export const HiddenCSVLink = styled(Box)(() => ({
  display: 'none'
}))
